import { ArrowUpRight, Sparkles } from 'lucide-react';
import { useBrand, useSubscription } from '../api/queries.ts';
import type { SubscriptionDetail } from '../api/types.ts';
import { Button } from '../components/ui/button.tsx';
import { RowList } from '../components/ui/card.tsx';
import { EmptyNote, ErrorNote, Loading, PageTitle, errorMessage } from '../components/ui/feedback.tsx';
import { manageSubscriptionUrl } from '../lib/brand.ts';
import { cn } from '../lib/cn.ts';
import { cancelGuides } from './cancelGuides.ts';

const STORE_NAMES: Record<string, string> = { stripe: 'card, through Stripe', apple: 'the App Store', google: 'Google Play' };
const SOURCE_NAMES: Record<string, string> = { team: 'Team plan' };

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function storeName(store?: string): string | null {
  if (!store) return null;
  return STORE_NAMES[store.toLowerCase()] ?? store;
}

function capitalise(text: string): string {
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}

export function SubscriptionPage() {
  const subscription = useSubscription();
  const brand = useBrand();
  const manage = brand.data ? manageSubscriptionUrl(brand.data) : null;
  const data = subscription.data;

  return (
    <>
      <PageTitle
        description="Your plan, and where to change or cancel it."
        aside={
          manage && data?.subscribed ? (
            <Button asChild track="subscription_manage">
              <a href={manage} target="_blank" rel="noreferrer">
                Manage subscription <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
            </Button>
          ) : null
        }
      >
        Subscription
      </PageTitle>
      {subscription.isPending ? <Loading /> : null}
      {subscription.isError ? <ErrorNote title="Could not load your subscription">{errorMessage(subscription.error)}</ErrorNote> : null}
      {data ? (
        data.subscriptions.length > 0 ? (
          <div className="flex flex-col gap-4">
            {data.subscriptions.map((item) => (
              <Plan key={`${item.domain}:${item.packageId ?? ''}`} item={item} brandName={brand.data?.name ?? ''} />
            ))}
          </div>
        ) : (
          <EmptyNote icon={<Sparkles className="h-5 w-5" />} title="You are on the free plan">
            No paid subscription was found for this account.
          </EmptyNote>
        )
      ) : null}

      <section className="mt-12">
        <h2 className="font-display text-lg font-semibold text-ink">Cancel your subscription</h2>
        <p className="mt-1 mb-4 text-sm text-muted">A subscription is cancelled where it was bought. A subscription bought on a phone is cancelled in that store.</p>
        <RowList>
          {cancelGuides(manage).map((guide) => (
            <li key={guide.key}>
              <a
                href={guide.href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-paper sm:px-6"
              >
                <span>
                  <span className="block font-semibold text-ink">{guide.label}</span>
                  <span className="block text-sm text-muted">{guide.detail}</span>
                </span>
                <ArrowUpRight className="h-[18px] w-[18px] shrink-0 text-muted" aria-hidden />
              </a>
            </li>
          ))}
        </RowList>
      </section>
    </>
  );
}

function Plan({ item, brandName }: { item: SubscriptionDetail; brandName: string }) {
  const active = item.status.toLowerCase() === 'active' || item.status.toLowerCase() === 'trialing';
  const store = storeName(item.store);
  return (
    <section className="rounded-[1.5rem] bg-brand p-7 text-brand-foreground sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium opacity-80">{brandName}</p>
          <p className="font-display text-[2rem] leading-tight font-semibold">{SOURCE_NAMES[item.source] ?? capitalise(item.source)}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold',
            active ? 'bg-surface text-ok' : 'bg-white/20 text-brand-foreground',
          )}
        >
          {active ? <span className="h-1.5 w-1.5 rounded-full bg-ok" aria-hidden /> : null}
          {capitalise(item.status)}
        </span>
      </div>
      <dl className="mt-8 grid grid-cols-2 gap-5 text-sm sm:grid-cols-3">
        <div>
          <dt className="opacity-75">{item.cancelAtPeriodEnd ? 'Ends on' : 'Renews on'}</dt>
          <dd className="mt-0.5 font-semibold">{formatDate(item.validUntil)}</dd>
        </div>
        {store ? (
          <div>
            <dt className="opacity-75">Paid by</dt>
            <dd className="mt-0.5 font-semibold">{capitalise(store)}</dd>
          </div>
        ) : null}
        <div>
          <dt className="opacity-75">Covers</dt>
          <dd className="mt-0.5 font-semibold">{item.domain}</dd>
        </div>
      </dl>
    </section>
  );
}
